/**
 * Tender API Service
 * 
 * Provides standardized methods for interacting with the Tender API endpoints
 */
import api from './api';
import { useAuthStore } from '../stores/auth';

/**
 * Tender service with standardized methods for tender-related API operations
 */
export const tenderService = {
  /**
   * Get all tenders with optional filtering and pagination
   * @param {Object} params - Query parameters (page, limit, filter, etc.)
   * @returns {Promise<Object>} Paginated tenders with metadata
   */
  async getTenders(params = {}) {
    return api.getPaginated('tenders', params);
  },

  /**
   * Get a single tender by ID with optional includes
   * @param {number|string} id - Tender ID
   * @param {Array|string} include - Related data to include
   * @returns {Promise<Object>} Tender data
   */
  async getTender(id, include = []) {
    const params = {};
    if (include) {
      params.include = include;
    }
    return api.get(`tenders/${id}`, params);
  },

  /**
   * Create a new tender
   * @param {Object} tenderData - Tender data
   * @returns {Promise<Object>} Created tender
   */
  async createTender(tenderData) {
    return api.post('tenders', tenderData);
  },

  /**
   * Update an existing tender
   * @param {number|string} id - Tender ID
   * @param {Object} tenderData - Updated tender data
   * @returns {Promise<Object>} Updated tender
   */
  async updateTender(id, tenderData) {
    return api.put(`tenders/${id}`, tenderData);
  },

  /**
   * Delete a tender
   * @param {number|string} id - Tender ID
   * @returns {Promise<Object>} Deletion result
   */
  async deleteTender(id) {
    return api.delete(`tenders/${id}`);
  },

  /**
   * Add a contractor to a tender
   * @param {number|string} tenderId - Tender ID
   * @param {Object} contractorData - Contractor data
   * @returns {Promise<Object>} Added contractor
   */
  async addContractor(tenderId, contractorData) {
    return api.post(`tenders/${tenderId}/contractors`, contractorData);
  },

  /**
   * Update a contractor in a tender
   * @param {number|string} tenderId - Tender ID
   * @param {number|string} contractorId - Contractor ID
   * @param {Object} contractorData - Updated contractor data
   * @returns {Promise<Object>} Updated contractor
   */
  async updateContractor(tenderId, contractorId, contractorData) {
    return api.put(`tenders/${tenderId}/contractors/${contractorId}`, contractorData);
  },

  /**
   * Remove a contractor from a tender
   * @param {number|string} tenderId - Tender ID
   * @param {number|string} contractorId - Contractor ID
   * @returns {Promise<Object>} Removal result
   */
  async removeContractor(tenderId, contractorId) {
    return api.delete(`tenders/${tenderId}/contractors/${contractorId}`);
  },

  /**
   * Add a quote to a tender
   * @param {number|string} tenderId - Tender ID
   * @param {Object} quoteData - Quote data
   * @returns {Promise<Object>} Added quote
   */
  async addQuote(tenderId, quoteData) {
    return api.post(`tenders/${tenderId}/quotes`, quoteData);
  },

  /**
   * Update a quote in a tender
   * @param {number|string} tenderId - Tender ID
   * @param {number|string} quoteId - Quote ID
   * @param {Object} quoteData - Updated quote data
   * @returns {Promise<Object>} Updated quote
   */
  async updateQuote(tenderId, quoteId, quoteData) {
    return api.put(`tenders/${tenderId}/quotes/${quoteId}`, quoteData);
  },

  /**
   * Remove a quote from a tender
   * @param {number|string} tenderId - Tender ID
   * @param {number|string} quoteId - Quote ID
   * @returns {Promise<Object>} Removal result
   */
  async removeQuote(_tenderId, quoteId) {
    return api.delete(`quotes/${quoteId}`);
  },

  /**
   * Add a comment to a tender
   * @param {number|string} tenderId - Tender ID
   * @param {Object} commentData - Comment data
   * @returns {Promise<Object>} Added comment
   */
  async addComment(tenderId, commentData) {
    return api.post(`tenders/${tenderId}/comments`, commentData);
  },

  /**
   * Get activities for a tender
   * @param {number|string} tenderId - Tender ID
   * @param {Object} params - Query parameters (page, limit)
   * @returns {Promise<Object>} Tender activities
   */
  async getActivities(tenderId, params = {}) {
    return api.getPaginated(`tenders/${tenderId}/activities`, params);
  },

  /**
   * Send a tender to contractors
   * @param {number|string} tenderId - Tender ID
   * @param {number|string} quoteId - Quote ID
   * @param {Object} sendData - Send options (recipients, message)
   * @returns {Promise<Object>} Send result
   */
  async sendTender(tenderId, _quoteId, sendData) {
    // Note: quoteId reserved for future use when API supports it
    return api.post(`tenders/${tenderId}/send`, sendData);
  },

  /**
   * Upload a document for a tender contractor
   * @param {number|string} tenderId - Tender ID
   * @param {number|string} contractorId - Contractor ID
   * @param {FormData} formData - Form data with file and metadata
   * @returns {Promise<Object>} Upload result
   */
  async uploadContractorDocument(tenderId, contractorId, formData) {
    // Create custom headers without Content-Type to let browser set it with boundary
    const headers = api.getHeaders();
    delete headers['Content-Type'];
    
    // Custom request for file upload
    try {
      const url = `${'/api'}/tenders/${tenderId}/contractors/${contractorId}/documents`;
      const response = await fetch(url, {
        method: 'POST',
        headers,
        credentials: 'include',
        body: formData,
      });
      
      return api.handleResponse(response);
    } catch (error) {
      throw api.handleError(error);
    }
  },

  /**
   * Delete a contractor document
   * @param {number|string} tenderId - Tender ID
   * @param {number|string} contractorId - Contractor ID
   * @param {number|string} documentId - Document ID
   * @returns {Promise<Object>} Deletion result
   */
  async deleteContractorDocument(tenderId, contractorId, documentId) {
    return api.delete(`tenders/${tenderId}/contractors/${contractorId}/documents/${documentId}`);
  },

  /**
   * Get contractor document download URL
   * @param {number|string} tenderId - Tender ID
   * @param {number|string} contractorId - Contractor ID
   * @param {number|string} documentId - Document ID
   * @returns {string} Document download URL with authentication token
   */
  getContractorDocumentDownloadUrl(tenderId, contractorId, documentId) {
    // For file downloads we need to use token as query param
    const authStore = useAuthStore();
    const token = authStore.token;
    return `/api/tenders/${tenderId}/contractors/${contractorId}/documents/${documentId}/download?token=${token}`;
  },

  /**
   * Get sections for a tender quote
   * @param {number|string} tenderId - Tender ID
   * @param {number|string} quoteId - Quote ID
   * @returns {Promise<Object>} Tender quote sections
   */
  async getQuoteSections(tenderId, quoteId) {
    return api.get(`tenders/${tenderId}/quotes/${quoteId}/sections`);
  },

  /**
   * Update sections for a tender quote
   * @param {number|string} tenderId - Tender ID
   * @param {number|string} quoteId - Quote ID
   * @param {Object} sectionsData - Updated sections data
   * @returns {Promise<Object>} Update result
   */
  async updateQuoteSections(tenderId, quoteId, sectionsData) {
    return api.put(`tenders/${tenderId}/quotes/${quoteId}/sections`, sectionsData);
  },

  // ==================== Followups ====================

  /**
   * Get followups for a tender
   * @param {number|string} tenderId - Tender ID
   * @returns {Promise<Array>} List of followups
   */
  async getFollowups(tenderId) {
    return api.get(`tenders/${tenderId}/followups`);
  },

  /**
   * Create a followup for a tender
   * @param {number|string} tenderId - Tender ID
   * @param {Object} followupData - Followup data
   * @param {string} followupData.followup_date - Date of followup
   * @param {number|null} [followupData.organization_id] - Organization contacted
   * @param {string|null} [followupData.comments] - Comments/notes
   * @param {string|null} [followupData.outcome] - Outcome (positive, negative, no_response, info_gathered)
   * @param {string|null} [followupData.next_followup_date] - Next followup date
   * @returns {Promise<Object>} Created followup
   */
  async createFollowup(tenderId, followupData) {
    return api.post(`tenders/${tenderId}/followups`, followupData);
  },

  /**
   * Update a followup
   * @param {number|string} tenderId - Tender ID
   * @param {number|string} followupId - Followup ID
   * @param {Object} followupData - Updated followup data
   * @returns {Promise<Object>} Updated followup
   */
  async updateFollowup(tenderId, followupId, followupData) {
    return api.put(`tenders/${tenderId}/followups/${followupId}`, followupData);
  },

  /**
   * Delete a followup
   * @param {number|string} tenderId - Tender ID
   * @param {number|string} followupId - Followup ID
   * @returns {Promise<Object>} Deletion result
   */
  async deleteFollowup(tenderId, followupId) {
    return api.delete(`tenders/${tenderId}/followups/${followupId}`);
  },

  /**
   * Get quote options for a tender
   * @param {number|string} tenderId - Tender ID
   * @returns {Promise<Array>} Quote options with latest versions
   */
  async getQuoteOptions(tenderId) {
    return api.get(`tenders/${tenderId}/quote-options`);
  },

  // ==================== Market Sectors ====================

  /**
   * Get all active market sectors
   * @returns {Promise<Array>} List of market sectors
   */
  async getMarketSectors() {
    return api.get('/market-sectors');
  },
};

export default tenderService;